/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import moment from 'moment-timezone';

import { action } from '@ember/object';
import Component from '@glimmer/component';

import type { TSize } from 'volta/utils/style-utils';
import type { PowerCalendarDay, SelectedPowerCalendarRange } from 'ember-power-calendar/utils';
import type { PowerCalendarAPI } from 'ember-power-calendar/components/power-calendar';
import type { TDate } from 'volta/models/types/calendar';
import type BvCalendarDays from './days';
import type BvCalendarNavComponent from './nav';

export interface BvCalendarSignature {
  Args: {
    selected?: TDate | SelectedPowerCalendarRange;
    center?: TDate;
    withRange?: boolean;
    size?: Extract<TSize, 'sm' | 'md' | 'lg'>;
    minDate?: TDate;
    maxDate?: TDate;
    fullWidth?: boolean;
    minRange?: number;
    showNextMonth?: boolean;
    onCenterChange?: (center: PowerCalendarDay) => void;
    onSelect?: (
      day: PowerCalendarDay,
      calendar: PowerCalendarAPI,
      e: InputEvent,
      afterSelect?: (e: InputEvent) => void
    ) => void;
    afterSelect?: (e: InputEvent) => void;
  };
  Blocks: {
    default: [
        PowerCalendarAPI & {
        minDate?: Date;
        maxDate?: Date;
        Days: typeof BvCalendarDays;
        Nav: typeof BvCalendarNavComponent;
      }
    ];
  };
  Element: HTMLElement;
}

export default class BvCalendarComponent extends Component<BvCalendarSignature> {
  get center(): Date {
    return moment.utc(this.args.center).toDate();
  }

  get selected(): TDate | SelectedPowerCalendarRange | undefined {
    const { selected, withRange } = this.args;
    if (!selected) {
      return undefined;
    }
    if (withRange) {
      return selected;
    }

    return moment.utc(selected as Date).toDate();
  }

  get nextMonthsCenter() {
    return moment.utc(this.center).add(1, 'month');
  }

  @action
  handleSelect(day: PowerCalendarDay, calendar: any, e: InputEvent) {
    this.args.onSelect?.(day, calendar, e, this.args.afterSelect);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    BvCalendar: typeof BvCalendarComponent;
    'bv-calendar': typeof BvCalendarComponent;
  }
}
